/**
 * @file        fetch-uri.js
 * @author      Nazila Akhavan <nazila@kingsds.network>
 *              Wes Garland <wes@kingsds.network>
 *              Kirill Kirnichansky <kirill@distributive.network>
 * @date        Sep 2020, Nov 2020, June 2023
 *
 * Fetch URLs/ Data that is stored in the database.
 * Bootstrap some our own needs via custom MIME Types in data URLs.
 */
// @ts-check
'use strict';

const { assert } = require('dcp/common/dcp-assert');
const kvin = require('kvin');
const scopedKvin = new kvin.KVIN({Object: ({}).constructor,
                                  Array: ([]).constructor, 
                                  Function: (()=>{}).constructor});

const { reallyJustFetch } = require('./just-fetch');
const { DCPError } = require('dcp/common/dcp-error');
const { atob } = require('dcp/utils/content-encoding');

/** @typedef {import('dcp/dcp-client/worker/origin-access-manager').OriginAccessManager} OriginAccessManager */

/**
 * Fetch the data via HTTP GET at the given URI. data: URIs are decoded directly, 
 * http: or https: URIs are fetched via GET queries. 
 *
 * data: URIs will always be decoded.
 * Other URIs (eg https:) must have their origins stored in the OriginAccessManager.
 * 
 * If URI is a URL and OAM is not provided, this will throw.
 * 
 * If originManager is false, origins will NOT be checked against some 'allow list'. This condition
 * bypasses the checking of origins if its not desired. References to such calls to fetchURI are found
 * on the job/result handle. 
 *
 * The return type is unpacked by justFetch - maybe not the best place for this - but this means
 * that we will return a promise that resolves to many possible JS types, dependening on the MIME
 * content-type of the response. The same observation holds for the content-type inside any data:
 * URIs.
 */

/**
 * @param {URL|string}  uri        - uri of the resource to be fetched
 * @param {OriginAccessManager|false} originManager - origin manager (needed for non-data: URIs); if false, origins are not checked.
 * @param {string|null} purpose    - purpose of the origin
 * @param {string|null} [key=null] - key to specify which origins in the purpose are valid
 * @returns {Promise<object>}      - If successful, promise which resolves
 *          to the data at the URI; see justFetch() for details. Otherwise, promise which results to an
 *          instance of error. Errors which happen to due bad origins have the code EPERM_ORIGIN.
 */
exports.fetchURI = function fetchUri$$fetchURI(uri, originManager, purpose, key = null)
{
  return exports.reallyFetchURI(uri, originManager, purpose, key)
    .then((data) => {
      return exports.decodeData(data);
    });
}

/**
 * @param {URL|string}  uri        - uri of the resource to be fetched
 * @param {OriginAccessManager|false} originManager - origin manager (needed for non-data: URIs); if false, origins are not checked.
 * @param {string|null} purpose    - purpose of the origin
 * @param {string|null} [key=null] - key to specify which origins in the purpose are valid
 * @returns {Promise<object>}      - If successful, promise which resolves
 *          to the data at the URI; see justFetch() for details. Otherwise, promise which results to an
 *          instance of error. Errors which happen to due bad origins have the code EPERM_ORIGIN.
 */
exports.fetchURIAndLength = function fetchUri$$fetchURIAndLength(uri, originManager, purpose, key = null)
{
  return exports.reallyFetchURI(uri, originManager, purpose, key)
    .then((data) => {
      return { data: exports.decodeData(data), size: data.serializedData.length }
    });
}

/**
 * @param {URL|string}  uri        - uri of the resource to be fetched
 * @param {OriginAccessManager|false} originManager - origin manager (needed for non-data: URIs); if false, origins are not checked.
 * @param {string|null} purpose    - purpose of the origin
 * @param {string|null} [key=null] - key to specify which origins in the purpose are valid
 * @returns {Promise<object>}      - If successful, promise which resolves
 *          to the data at the URI; see justFetch() for details. Otherwise, promise which results to an
 *          instance of error. Errors which happen to due bad origins have the code EPERM_ORIGIN.
 */
exports.reallyFetchURI = async function fetchUri$$reallyFetchURI(uri, originManager, purpose, key = null)
{
  if ((typeof uri === 'object' && uri.protocol === 'data:') || (typeof uri === 'string' && uri.startsWith('data:')))
    return {
      serializedData: String(uri),
      contentType: 'application/x-dataurl', // Non-standard content type.
    };

  const url = typeof uri === 'string' ? new URL(uri) : uri;
  
  if (originManager !== false && !originManager)
    throw new DCPError('Not allowed to fetch origin without providing origin access manager.');
    
  if (url.protocol === 'file:')
  {
    if (originManager && !originManager.isAllowed(url.pathname, purpose, key))
      throw new DCPError(`Not allowed to fetch from the file path '${url.pathname}'`, 'EPERM_ORIGIN')
    
    return exports.parseFileURI(url);
  }

  if (originManager && !originManager.isAllowed(url.origin, purpose, key))
    throw new DCPError(`Not allowed to fetch from the origin '${url.origin}'`, 'EPERM_ORIGIN')

  return reallyJustFetch(url)
    .then((res) => {
      const { contentType, parameters } = exports.parseContentType(res.getResponseHeader('content-type'));
      return {
        serializedData: res.responseText,
        contentType,
        parameters,
      };
    });
}

/**
 * Parse contentTypeStr string.
 * @param {string} contentTypeStr
 * @returns {{ contentType: object, parameters: object }}
 */
exports.parseContentType = function fetchUri$$parseContentType (contentTypeStr)
{
  const mtArr = contentTypeStr.split(';');
  const contentType = new String(mtArr.shift());
  [ contentType['major'], contentType['minor'] ] = contentType.split('/');

  const parameters = {}
  for (const parameter of mtArr)
  {
    const [ attribute, value ] = parameter.split('=');
    parameters[attribute.toLowerCase()] = value ? value : true;
  }

  // When contentTypeStr specifies charset with no truey value, parameters.charset will be 'true'.
  parameters.charset = parameters.charset ? parameters.charset.toLowerCase() : false;

  return { contentType, parameters };
}

/**
 * Decodes/deserializes data depending on its contentType.
 * Expects an object returned by reallyFetchURI, with serializedData and contentType properties.
 * If object cannot be decoded, the serializedData is returned.
 * 
 * @param {{ serializedData: string, contentType: string, parameters:(object|undefined)}} encodedObj
 * @returns {string|object|Uint8Array}
 */
exports.decodeData = function utils$$decodeData (encodedObj)
{
  switch (String(encodedObj.contentType))
  {
    case 'text/plain':
      return encodedObj.serializedData;
    case 'application/json':
    case 'JSON':
      return JSON.parse(encodedObj.serializedData);
    case 'application/x-kvin':
      return scopedKvin.deserialize(encodedObj.serializedData);
    case 'application/x-dataurl': // Non-standard content type.
      return exports.parseDataURI(encodedObj.serializedData);
  }

  if (encodedObj.contentType.startsWith('text'))
    return exports.handleCharset(encodedObj.serializedData, encodedObj.parameters);

  // Return serializedData for other contentType.
  return encodedObj.serializedData;
}

/**
 * Instantiate an object based on a data: uri which has properties matching
 * the parameter attributes of the uri. Constructs an object with shape:
 *   {
 *     contentType: String,
 *     length: number,
 *     parameters: object, // kev-value pairs; e.g. { charset: 'utf8' }
 *     serializer: { method: string, parse: * },
 *   }
 * 
 * @param {string} uriString The data: URI to decode
 *
 * Object Properties:
 * - contentType:        boxed string which is the content type (eg 'image/png')
 * - contentType.major:  string which is the major part of the content type (eg 'image')
 * - contentType.minor:  string which is the minor part of the content type (eg 'png')
 * - length:             the length of the media type section of the data: URI
 * - parameters:         object which holds any parameters which were specified in
 *                       the URI; keys are lowercased attributes.
 * - parameters.charset: the charset used to encode data when contentType.major is 'text'
 *
 * @note    The parameters.charset property is treated a little differently to make
 *          it easier on API consumers; it is *always* defined, and lower case.
 *          If the charset was not specified, it is false.
 * @constructor
 */  
exports.MediaType = function fetchUri$$MediaType(uriString) {
  var mtStr = /(^data:)([^,]*)/.exec(uriString)[2];

  assert(uriString.startsWith('data:'));
  this.length = mtStr.length;
  if (mtStr === '')
    mtStr = 'text/plain;charset=US-ASCII';

  const { contentType, parameters } = exports.parseContentType(mtStr);
  this.contentType = contentType;
  this.parameters = parameters;

  /** @todo XXXpfr Verify whether 'text/plain' is correct here. */
  if (this.contentType == 'application/json' || this.contentType == 'text/plain')
  {
    this.serializer = {
      method: 'json',
      parse:  JSON.parse
    }
  }
  else if (this.contentType == 'application/x-kvin')
  {
    this.serializer = {
      method: 'kvin',
      parse:  scopedKvin.deserialize.bind(scopedKvin)
    }
  }
}

/**
 * Estimate the number of raw bytes stored in a data URI.  The estimate is pretty good for URIs
 * embedding base64. URIs which are made up of escaped characters are estimated by counting the number
 * of characters in the first part of the array, and then assuming that the encoding density is constant
 * through out. This is because the URI can be made up of bytes encoded with 1-or-3 bytes, but will 
 * normally be all either almost all 1 or all 3. Mixed would be pretty rare in the wild.
 *
 * This does mean that it's possible to under-estimate by nearly 66%, so backing store should be ready
 * for this, but it's realllly unlikely to happen.
 */
exports.estimateDataUriContentLength = function fetchUri$$estimateDataUriContentLength(uriString, mediaType)
{
  const headerLength = 5 + mediaType.length + 1; /* data: mediaType comma */

  if (!mediaType)
    mediaType = new exports.MediaType(uriString);
  if (mediaType.parameters.base64)
    return Math.ceil(((uriString.length - (mediaType.length + headerLength)) * 6) / 8);

  /* Assume that the distribution of escaped characters in the first bit is representative
   * of the URI as a whole. We don't want to take this apart just to estimate.
   */
  const leading  = uriString.slice(mediaType.length, 1024);
  const remain   = leading.replace(/%../g, '');
  const numEscs  = (leading.length - remain.length);
  const bytes    = remain.length + numEscs;
  const dilation = bytes / leading.length;
  
  return Math.ceil((uriString.length - headerLength) * dilation);
}

/**
 * Extract the raw data encoded a data URI.
 *
 * @param {string}      uriString       a valid data: URI
 * @param {exports.MediaType}   mediaType       optional instance of MediaType that describes this URI.
 *
 * @returns string or Buffer representing the RAW data. If the return type is a string, each character
 *          in the string will represent 1 byte, 0-255, of the raw data.
 */
exports.dataUri_extractBytes = function fetchUri$$dataUri_extractBytes(uriString, mediaType)
{
  if (!mediaType)
    mediaType = new exports.MediaType(uriString);

  const payload = uriString.slice(5 + mediaType.length + 1); /* data: mediaType comma */
  const data = mediaType.parameters.base64 ? atob(payload) : decodeURI(payload);

  return data;
}

/**
 * Extract text encoded in a data URI.
 *
 * @param {string}            uriString       a valid data: URI
 * @param {exports.MediaType} mediaType       optional instance of MediaType that describes this URI.
 *
 * @returns a JS-style Unicode string, converting from whatever charset is indicated in the
 *          data URI's content-type charset attribute.
 */
exports.dataUri_extractText = function fetchUri$$dataUri_extractText(uriString, mediaType)
{
  if (!mediaType)
    mediaType = new exports.MediaType(uriString);

  switch (mediaType.parameters.charset)
  {
    default:
      throw new Error(`Character set ${mediaType.parameters.charset} not supported`);

    case undefined:
    case false:
    case 'iso-8859-1': case 'latin1': case 'latin-1':
    case 'us-ascii':   case 'ascii':
      /* These character sets map directly onto unicode */
      return exports.dataUri_extractBytes(uriString, mediaType);
    case 'utf8': case 'utf-8':
      const payload = uriString.slice(5 + mediaType.length + 1); /* data: mediaType comma */
      return mediaType.parameters.base64 ? decodeURI(escape(atob(payload))) : decodeURI(payload);
  }

  throw 'unreached';
}

/**
 * Parses the file URI into an object containing the fetched data resource and its content type.
 * @param {URL} url
 * @returns {{serializedData: string, contentType: string }}
 */ 
exports.parseFileURI = function fetchUri$$reallyParseFileURI(url)
{
  var fetchedResource = require('fs').readFileSync(url, 'utf-8');
  var fileExt = require('path').extname(url.pathname);

  let contentType = "text/plain";
  switch (fileExt) {
    case '.kvin':
      contentType = "application/x-kvin";
      break;
    case '.json':
      contentType = "application/json";
      break;
  }

  return {
    serializedData: fetchedResource,
    contentType,
  }
}

/**
 * When parameters has a charset property, box the string and forward the property.
 * @param {string} unicodeString
 * @param {object} parameters
 * @returns {object}
 */
exports.handleCharset = function fetchUri$$handleCharset(unicodeString, parameters)
{
  /** @todo XXXpfr Figure out how to decode using parameters.charset . */
  const boxedUnicodeString = new String(unicodeString);
  if (parameters?.charset)
    boxedUnicodeString['charset'] = parameters.charset;
  return boxedUnicodeString;
}

/**
 * Parse a data: URI, returning the JavaScript value it encodes. The return type is selected
 * based on the content-type.
 *
 * <pre>
 * MIME Type                    Return Type
 * ------------------           ----------------------------------------------------------------------------------------------------
 * text/plain or none           string primitive
 * text/*                       A boxed string with the contentType property set, and the charset property set if it was specified.
 * application/json             whatever JSON.parse returns on the data when it is treated as a string
 * application/x-kvin           whatever kvin.deserialze returns on the data when it is treated as a string
 * image/*                      A Uint8Array of the decoded contents with the contentType property set.
 * *                            A Uint8Array of the decoded contents with the contentType property set and possibly the charset property.
 * </pre>
 *
 * @param {string}            uriString    the URI
 * @param {exports.MediaType} mediaType    optional instance of MediaType that describes this URI.
 * @returns {string|object|Uint8Array}
 */
exports.parseDataURI = function fetchUri$$parseDataURI(uriString, mediaType = null) {
  if (!mediaType)
    mediaType = new exports.MediaType(uriString);

  if (mediaType.contentType.major === 'text')
  {
    const unicodeString = exports.dataUri_extractText(uriString, mediaType);
    if (mediaType.contentType.minor === 'plain')
      return unicodeString;

    return exports.handleCharset(unicodeString, mediaType.parameters);
  }

  if (mediaType.serializer)
  {
    /* ALL supported serializers in DCP serialize to UTF-8 */
    const unicodeString = exports.dataUri_extractText(uriString, mediaType);
    return mediaType.serializer.parse(unicodeString);
  }

  const rawData = exports.dataUri_extractBytes(uriString, mediaType);
  const ui8 = Uint8Array.from(rawData, c => c.charCodeAt(0));
  ui8['contentType'] = mediaType.contentType;
  if (mediaType.contentType.major !== 'image' && mediaType.parameters.charset)
    ui8['charset'] = mediaType.parameters.charset;

  return ui8;
};
